import { SIGNIN_AUTOCOMPLETE, SIGNIN_LABELS, SIGNIN_PLACEHOLDERS } from '@constants'

/**
 * Field factory function to create consistent field definitions
 * Following Register pattern for field structure
 */
interface FieldConfig {
  id: string
  name: string
  label: string
  type: string
  required: boolean
  placeholder?: string
  autoComplete?: string
  autoFocus?: boolean
  value?: string | boolean
  options?: Array<{ label: string; value: string }>
}

const createField = (config: FieldConfig) => ({
  id: config.id,
  name: config.name,
  label: config.label,
  type: config.type,
  required: config.required,
  placeholder: config.placeholder || '',
  autoComplete: config.autoComplete || '',
  autoFocus: config.autoFocus || false,
  value: config.value || (config.type === 'checkbox' ? false : ''),
  options: config.options || [],
})

export const signinStepFields = {
  username: createField({
    id: 'username',
    name: 'username',
    label: SIGNIN_LABELS.USERNAME,
    type: 'text',
    required: true,
    placeholder: SIGNIN_PLACEHOLDERS.USERNAME,
    autoComplete: SIGNIN_AUTOCOMPLETE.USERNAME,
    autoFocus: true,
  }),
  password: createField({
    id: 'password',
    name: 'password',
    label: SIGNIN_LABELS.PASSWORD,
    type: 'password',
    required: true,
    placeholder: SIGNIN_PLACEHOLDERS.PASSWORD,
    autoComplete: SIGNIN_AUTOCOMPLETE.PASSWORD,
  }),
  rememberMe: createField({
    id: 'rememberMe',
    name: 'rememberMe',
    label: SIGNIN_LABELS.REMEMBER_ME,
    type: 'checkbox',
    required: false,
  }),
  submitButton: createField({
    id: 'signInSubmit',
    name: 'signInSubmit',
    label: SIGNIN_LABELS.SIGN_IN_SUBMIT_BUTTON_TITLE,
    type: 'submit',
    required: true,
  }),
}

// Field definitions for the help panel step
export const helpPanelStepFields = {
  email: createField({
    id: 'email',
    name: 'email',
    label: SIGNIN_LABELS.EMAIL_ADDRESS,
    type: 'email',
    required: true,
    placeholder: SIGNIN_PLACEHOLDERS.EMAIL,
    autoComplete: 'email',
  }),
  username: createField({
    id: 'username',
    name: 'username',
    label: SIGNIN_LABELS.USERNAME,
    type: 'text',
    required: true,
    placeholder: SIGNIN_PLACEHOLDERS.USERNAME,
    autoComplete: 'username',
  }),
  verificationMethod: createField({
    id: 'verificationMethod',
    name: 'verificationMethod',
    label: SIGNIN_LABELS.VERIFICATION_METHOD,
    type: 'select',
    required: true,
    value: 'email',
    options: [
      { label: SIGNIN_LABELS.EMAIL_LABEL, value: 'email' },
      { label: SIGNIN_LABELS.PHONE_LABEL, value: 'phone' },
    ],
  }),
  registerAsCompany: createField({
    id: 'registerAsCompany',
    name: 'registerAsCompany',
    label: SIGNIN_LABELS.REGISTER_AS_COMPANY,
    type: 'checkbox',
    required: false,
    value: false,
  }),
  ownerNumber: createField({
    id: 'ownerNumber',
    name: 'ownerNumber',
    label: SIGNIN_LABELS.OWNER_NUMBER,
    type: 'text',
    required: true,
    placeholder: SIGNIN_PLACEHOLDERS.OWNER_NUMBER,
    autoComplete: 'off',
  }),
  companyName: createField({
    id: 'companyName',
    name: 'companyName',
    label: SIGNIN_LABELS.COMPANY_NAME_LABEL,
    type: 'text',
    required: false,
    placeholder: SIGNIN_PLACEHOLDERS.COMPANY_NAME,
    autoComplete: 'organization',
  }),
}

// Field definitions for the forgot password reset form step
export const forgotPasswordResetFormFields = {
  newPassword: createField({
    id: 'newPassword',
    name: 'newPassword',
    label: SIGNIN_LABELS.NEW_PASSWORD_LABEL,
    type: 'password',
    required: true,
    placeholder: SIGNIN_PLACEHOLDERS.NEW_PASSWORD,
    autoComplete: 'new-password',
  }),
  confirmPassword: createField({
    id: 'confirmPassword',
    name: 'confirmPassword',
    label: SIGNIN_LABELS.CONFIRM_PASSWORD,
    type: 'password',
    required: true,
    placeholder: SIGNIN_PLACEHOLDERS.CONFIRM_PASSWORD,
    autoComplete: 'new-password',
  }),
}

export { createField }
export type { FieldConfig }
